import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { getOrderDetail } from '~/services/OrdersService';

export interface OrderBreadcrumbData {
  user: {
    label: string;
    href: string;
  };
  order: {
    label: string;
    href: string;
    brand: string;
  };
}

interface UseOrderBreadcrumbsParams {
  orderId: string | null;
  enabled?: boolean;
}

interface UseOrderBreadcrumbsResult {
  data: OrderBreadcrumbData | null;
  isLoading: boolean;
  error: Error | null;
}

function formatCurrency(amount: number, currencyCode: string): string {
  try {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${currencyCode} ${amount.toLocaleString()}`;
  }
}

function formatOrderDate(value: string): string {
  return dayjs(value).format("D MMM 'YY");
}

function formatOrderLabel(order: App.Order): string {
  const total = order.total ?? 0;
  const currency = order.currency_code || 'AUD';
  const date = order.created_at ? formatOrderDate(order.created_at) : '';

  const formattedTotal = formatCurrency(total, currency);
  return date ? `${formattedTotal} on ${date}` : formattedTotal;
}

async function fetchOrderBreadcrumbData(orderId: string): Promise<OrderBreadcrumbData> {
  const order = await getOrderDetail(orderId);

  const customerId = order.fk_customer_id;
  const customerName = order.customer_full_name || order.customer_email || 'Customer';

  return {
    user: {
      label: customerName,
      href: `/users/${customerId}`,
    },
    order: {
      label: formatOrderLabel(order),
      href: `/purchases/${orderId}`,
      brand: order.brand,
    },
  };
}

export default function useOrderBreadcrumbs({
  orderId,
  enabled = true,
}: UseOrderBreadcrumbsParams): UseOrderBreadcrumbsResult {
  const isEnabled = enabled && Boolean(orderId);

  const { data, isLoading, error } = useQuery<OrderBreadcrumbData>({
    queryKey: ['order-breadcrumbs', orderId],
    queryFn: async () => {
      if (!orderId) {
        throw new Error('Order ID is required');
      }
      return fetchOrderBreadcrumbData(orderId);
    },
    enabled: isEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });

  return {
    data: data ?? null,
    isLoading,
    error: error as Error | null,
  };
}
